import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { UserProfileEvents } from "@app/capacitacion-tecnicos/core/models/user";
import { normalizeRoleName } from "../data/gu-roles";
import { IPermissions, TypeUserPermissions } from "../interfaces/permissions";
import { IPersonCompanyProfile } from "../interfaces/person-company-profile";
import { environment } from "src/environments/environment";
import { tap } from "rxjs/operators";

const httpOptions = {
	headers: new HttpHeaders({ "Content-Type": "application/json" }),
};

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private _basePathEventos = environment.urlEventosCampos;
	private _basePathGestion = environment.urlGestionUsuarios;
	private _userPermissions = new BehaviorSubject<TypeUserPermissions>([]);
	private _permissions = new BehaviorSubject<IPermissions[]>([]);
	private _userProfileEvents = new BehaviorSubject<UserProfileEvents | null>(
		null
	);
	private _personCompanyProfile = new BehaviorSubject<IPersonCompanyProfile | null>(null);

	userPermissions = this._userPermissions.asObservable();
	permissions = this._permissions.asObservable();
	public userLoggedIn$ = this._userProfileEvents.asObservable();
	public personCompanyProfile$ = this._personCompanyProfile.asObservable();

	constructor(private http: HttpClient) {}

	// with value
	getUserPermissions() {
		return this._userPermissions.value;
	}

	// with getValue
	getValueUserPermissions() {
		return this._userPermissions.getValue();
	}

	getUserRoles(): Observable<any> {
		return this.http
			.get(`${this._basePathEventos}/users/profile/`)
			.pipe(tap((data) => this._handleSuccessProfile(data)));
	}

	setUserPermissions(permissions: TypeUserPermissions) {
		this._userPermissions.next(permissions);
	}

	checkUserPermissions(possiblePermissions: TypeUserPermissions): boolean {
		// si el array es empty entonces tiene acceso al recurso
		if (!possiblePermissions.length) return true;

		return possiblePermissions.some((permission) =>
			this._userPermissions.value.includes(permission.toUpperCase())
		);
	}

	updateUserProfile(data: any) {
		this._handleSuccessProfile(data);
	}

	get currentUserProfileEvents(): UserProfileEvents {
		return (
			this._userProfileEvents.value ||
			JSON.parse(localStorage.getItem("user-data"))
		);
	}

	private _handleSuccessProfile(data: any) {
		this._userProfileEvents.next(data);
		localStorage.setItem("user-data", JSON.stringify(data));
	}

	getPersonCompanyProfile(): Observable<IPersonCompanyProfile> {
		return this.http
			.get<IPersonCompanyProfile>(`${this._basePathGestion}/api/person/profile/`)
			.pipe(tap((data) => this._handleSuccessPersonCompanyProfile(data)));
	}

	private _handleSuccessPersonCompanyProfile(data: IPersonCompanyProfile) {
		this._personCompanyProfile.next(data);
		localStorage.setItem("personcompany-data", JSON.stringify(data));
	}

	get currentPersonCompanyProfile(): IPersonCompanyProfile | null {
		return (
			this._personCompanyProfile.value ||
			JSON.parse(localStorage.getItem("personcompany-data") || "null")
		);
	}

	updatePersonCompanyProfile(data: IPersonCompanyProfile) {
		this._handleSuccessPersonCompanyProfile(data);
	}

	/**
	 * @deprecated Los ids de rol de Gestión Usuarios no son estables entre
	 * entornos: un mismo rol tiene distinto id en desarrollo y en producción.
	 * Usar {@link hasAnyRoleName} y comparar por nombre.
	 */
	hasRole(roleId: number): boolean {
		return (
			this.currentPersonCompanyProfile?.roles?.some((r) => r.id === roleId) ?? false
		);
	}

	/**
	 * @deprecated Los ids de rol de Gestión Usuarios no son estables entre
	 * entornos. Usar {@link hasAnyRoleName} y comparar por nombre.
	 */
	hasAnyRole(roleIds: number[]): boolean {
		const roles = this.currentPersonCompanyProfile?.roles ?? [];
		return roles.some((r) => roleIds.includes(r.id));
	}

	/**
	 * Indica si el usuario tiene alguno de los roles indicados, comparando por
	 * NOMBRE normalizado.
	 *
	 * Es el método a emplear en toda decisión de autorización. Los nombres se
	 * importan de `GU_ROLES`; nunca se escriben como literal.
	 *
	 * Falla cerrado: devuelve `false` ante una lista vacía, un perfil ausente o
	 * un usuario sin roles. Una decisión de autorización sin criterio debe
	 * denegar, no conceder.
	 *
	 * @param roleNames nombres de rol que conceden la autorización
	 */
	hasAnyRoleName(roleNames: string[]): boolean {
		const wanted = (roleNames ?? []).map(normalizeRoleName).filter(Boolean);
		if (!wanted.length) return false;

		const userRoles = this.currentPersonCompanyProfile?.roles ?? [];

		return userRoles.some((role) => {
			const name = normalizeRoleName(role?.name);
			return !!name && wanted.includes(name);
		});
	}

	/**
	 * Guarda los permisos filtrados como objeto IPermissions
	 * @param permissions {IPermissions}
	 */
	setUserPermissionsAsObject(permissions: IPermissions[]) {
		this._permissions.next(permissions);
	}

	// with value
	getPermissions() {
		return this._permissions.value;
	}

	// with getValue
	getValuePermissions() {
		return this._permissions.getValue();
	}
}
